import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { formatMoney } from '../lib/format.js'
import { effectiveUnitCost } from '../hooks/useRecipesData.js'
import KitchenLoader from './KitchenLoader.jsx'
import ItemPicker from './ItemPicker.jsx'

// AI-assisted recipe builder. Manager describes a dish, Claude
// (via the build-recipe Edge Function) returns a structured recipe
// with components matched to existing catalog items, COGS, and a
// suggested menu price. Manager reviews, edits, accepts.
//
// Mirrors the proven InvoiceScanModal / ScheduleSuggestModal pattern:
//   inputs    — dish description + yield + target food cost
//   generating — calls the edge function
//   review    — show proposal, allow edit/remove of components,
//               recompute totals live as edits happen
//   submitting — bulk-insert recipe + components
//   error     — retry path
export default function RecipeBuildModal({
  open,
  onClose,
  onAccepted,        // ({ name, count }) → void
  items,             // catalog from useSettingsData — for matching + display
  recipes,           // existing recipes (for multi-recipe warning on inline picker)
  createRecipe,      // from useRecipesData — does the parent + components insert
  onNavigateToItems, // empty-state CTA from inline ItemPicker
}) {
  const [phase, setPhase] = useState('inputs')
  const [error, setError] = useState(null)

  // Inputs
  const [description, setDescription] = useState('')
  const [yieldServings, setYieldServings] = useState('1')
  const [targetPct, setTargetPct] = useState('30')

  // AI response
  const [proposal, setProposal] = useState(null)
  // Editable component rows (start from proposal, manager tweaks)
  const [editedComponents, setEditedComponents] = useState([])
  const [editedName, setEditedName] = useState('')
  const [removedComponentIds, setRemovedComponentIds] = useState(new Set())
  const [linkedItemId, setLinkedItemId] = useState(null)

  // Reset on open
  useEffect(() => {
    if (open) {
      setPhase('inputs')
      setError(null)
      setProposal(null)
      setEditedComponents([])
      setEditedName('')
      setRemovedComponentIds(new Set())
      setDescription('')
      setYieldServings('1')
      setTargetPct('30')
      setLinkedItemId(null)
    }
  }, [open])

  // ---------- ALL useMemo hooks above the early return ----------
  // (Rules-of-Hooks pattern lesson — same as ScheduleSuggestModal)

  // Map items by id for quick lookup during recompute
  const itemsById = useMemo(() => {
    const m = new Map()
    for (const it of items ?? []) m.set(it.id, it)
    return m
  }, [items])

  // Multi-recipe warning data for the inline ItemPicker — mirrors
  // RecipeLinkModal so the AI Build flow surfaces the same amber
  // "Already linked to recipe X" hint when a manager picks an item
  // that already has a recipe. New-recipe flow has no "current recipe
  // to exclude", so we include every recipe with an item_id.
  const recipesByItemId = useMemo(() => {
    const m = new Map()
    for (const r of recipes || []) {
      if (!r.item_id) continue
      const list = m.get(r.item_id) || []
      list.push(r)
      m.set(r.item_id, list)
    }
    return m
  }, [recipes])

  // Visible components = edited - removed
  const visibleComponents = useMemo(() => {
    return editedComponents.filter((c) => !removedComponentIds.has(c._id))
  }, [editedComponents, removedComponentIds])

  // Live cost recompute from current item costs (not the AI's snapshot)
  // so manager sees accurate dollars after editing quantities. Uses
  // effectiveUnitCost so a component "1 each" of a case-tracked item
  // costs cost_per_unit/pack_size, not cost_per_unit.
  const liveCost = useMemo(() => {
    let total = 0
    let known = true
    for (const c of visibleComponents) {
      const item = itemsById.get(c.item_id)
      const baseCost = Number(item?.cost_per_unit || 0)
      if (baseCost <= 0) known = false
      const effCost = effectiveUnitCost(item, c.unit)
      total += effCost * Number(c.quantity || 0)
    }
    const y = Number(yieldServings) || 1
    const perServing = y > 0 ? total / y : 0
    const pct = Number(targetPct) || 0
    const price = pct > 0 ? perServing / (pct / 100) : 0
    return { total, perServing, price, known }
  }, [visibleComponents, itemsById, yieldServings, targetPct])

  if (!open) return null

  // ---------- Generate ----------
  async function handleGenerate() {
    if (phase === 'generating') return
    if (!description.trim()) {
      setError('Describe the dish first.')
      return
    }
    if (!items || items.length === 0) {
      setError('Add items to your catalog first — the AI can only use ingredients you have on file.')
      return
    }
    setError(null)
    setPhase('generating')

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
      const { data: sess } = await supabase.auth.getSession()
      const token = sess?.session?.access_token || anonKey

      const payload = {
        dish_description:     description.trim(),
        yield_servings:       Number(yieldServings) || 1,
        target_food_cost_pct: Number(targetPct) || 30,
        linked_item_id:       linkedItemId,
        items: (items || []).map((i) => {
          const packSize = Number(i.pack_size || 1)
          const baseCost = Number(i.cost_per_unit || 0)
          return {
            id: i.id,
            name: i.name,
            category: i.category,
            unit: i.unit,
            cost_per_unit: baseCost,
            sale_price: i.sale_price != null ? Number(i.sale_price) : null,
            // Pack metadata so the AI can express components in the
            // inner unit when it makes sense for the recipe.
            pack_size: packSize,
            pack_unit: i.pack_unit || null,
            cost_per_pack_unit:
              packSize > 1 && i.pack_unit ? baseCost / packSize : null,
          }
        }),
      }

      const resp = await fetch(`${supabaseUrl}/functions/v1/build-recipe`, {
        method: 'POST',
        headers: {
          'content-type':  'application/json',
          'authorization': `Bearer ${token}`,
          'apikey':        anonKey,
        },
        body: JSON.stringify(payload),
      })

      if (!resp.ok) {
        const errText = await resp.text()
        throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
      }
      const data = await resp.json()
      if (data.error) {
        setError(data.message || 'AI could not build the recipe.')
        setPhase('error')
        return
      }
      // Tag each component with a stable id for remove-tracking
      const tagged = (data.components || []).map((c, idx) => ({
        ...c,
        _id: `c${idx}`,
      }))
      setProposal(data)
      setEditedComponents(tagged)
      const linkedItem = linkedItemId
        ? (items || []).find((i) => i.id === linkedItemId)
        : null
      setEditedName(linkedItem?.name || data.name || description.trim())
      setRemovedComponentIds(new Set())
      setPhase('review')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[RecipeBuildModal] generation failed', e)
      setError('Could not build recipe — please try again in a moment.')
      setPhase('error')
    }
  }

  // ---------- Review interactions ----------
  function updateComponent(id, patch) {
    setEditedComponents((prev) =>
      prev.map((c) => c._id === id ? { ...c, ...patch } : c)
    )
  }
  function removeComponent(id) {
    setRemovedComponentIds((prev) => {
      const next = new Set(prev)
      next.add(id)
      return next
    })
  }

  // ---------- Accept ----------
  // createRecipe comes from the parent's useRecipesData hook — we
  // call it via the prop in scope (not as an arg) so the button's
  // onClick handler doesn't accidentally pass the click event.
  async function handleAccept() {
    if (phase === 'submitting') return
    if (typeof createRecipe !== 'function') {
      setError('Internal error: recipe save handler not wired.')
      return
    }
    if (!editedName.trim()) {
      setError('Recipe name is required.')
      return
    }
    const componentsToSave = visibleComponents
      .filter((c) => c.item_id && Number(c.quantity) > 0)
    if (componentsToSave.length === 0) {
      setError('At least one component with a matched item and quantity > 0 is required.')
      return
    }

    setPhase('submitting')
    setError(null)
    try {
      await createRecipe({
        name:                 editedName.trim(),
        yield_servings:       Number(yieldServings) || 1,
        target_food_cost_pct: Number(targetPct) || null,
        notes:                proposal?.notes ?? null,
        item_id:              linkedItemId,
        components: componentsToSave.map((c) => ({
          item_id:  c.item_id,
          quantity: Number(c.quantity),
          unit:     c.unit || null,
          notes:    c.rationale || null,
        })),
      })
      onAccepted?.({ name: editedName.trim(), count: componentsToSave.length })
      onClose?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[RecipeBuildModal] accept failed', e)
      setError(`Could not save: ${e?.message || 'try again.'}`)
      setPhase('review')
    }
  }

  // ---------- Render ----------
  return (
    <RecipeBuildModalRender
      open={open}
      onClose={onClose}
      phase={phase}
      error={error}
      description={description}
      setDescription={setDescription}
      yieldServings={yieldServings}
      setYieldServings={setYieldServings}
      targetPct={targetPct}
      setTargetPct={setTargetPct}
      items={items}
      recipesByItemId={recipesByItemId}
      onNavigateToItems={onNavigateToItems}
      linkedItemId={linkedItemId}
      setLinkedItemId={setLinkedItemId}
      handleGenerate={handleGenerate}
      proposal={proposal}
      editedName={editedName}
      setEditedName={setEditedName}
      visibleComponents={visibleComponents}
      itemsById={itemsById}
      updateComponent={updateComponent}
      removeComponent={removeComponent}
      liveCost={liveCost}
      setPhase={setPhase}
      handleAccept={handleAccept}
    />
  )
}

// Render component — separate so the parent above can keep the
// hooks-only zone clean (per the Rules-of-Hooks pattern lesson).
function RecipeBuildModalRender({
  open, onClose, phase, error,
  description, setDescription,
  yieldServings, setYieldServings,
  targetPct, setTargetPct,
  items,
  recipesByItemId,
  onNavigateToItems,
  linkedItemId, setLinkedItemId,
  handleGenerate,
  proposal, editedName, setEditedName,
  visibleComponents, itemsById,
  updateComponent, removeComponent,
  liveCost, setPhase, handleAccept,
}) {
  if (!open) return null

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[95vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Build recipe"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">AI Recipe Builder</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          {/* INPUTS */}
          {phase === 'inputs' && (
            <>
              <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-3 text-sm leading-relaxed text-blue-900/90">
                Describe a dish and the AI will break it into ingredient
                components from your catalog, compute COGS, and suggest
                a menu price. You'll review and edit before saving.
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="r-desc">
                  Dish description
                </label>
                <textarea
                  id="r-desc"
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder='e.g. "Brisket sandwich on a brioche bun with pickles and signature sauce. About 5 oz brisket per sandwich."'
                  className="w-full rounded-lg border border-black/15 px-4 py-3 text-base outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
                />
              </div>

              <div>
                <div className="mb-1 block text-sm font-medium">
                  Sold as menu item
                </div>
                <ItemPicker
                  mode="inline"
                  items={items}
                  value={linkedItemId}
                  onSelect={(it) => setLinkedItemId(it.id)}
                  onClear={() => setLinkedItemId(null)}
                  recipesByItemId={recipesByItemId}
                  onNavigateToItems={onNavigateToItems}
                />
                {!linkedItemId && (
                  <p className="mt-1 text-xs text-black/55">
                    Optional — leave blank if you'll link this recipe to a menu item later.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium" htmlFor="r-yield">
                    Yield (servings)
                  </label>
                  <input
                    id="r-yield"
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    min="0.1"
                    value={yieldServings}
                    onChange={(e) => setYieldServings(e.target.value)}
                    className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600"
                  />
                  <p className="mt-1 text-xs text-black/55">
                    1 = single dish. Higher numbers for batch recipes (sauces, prep batches).
                  </p>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium" htmlFor="r-target">
                    Target food cost %
                  </label>
                  <input
                    id="r-target"
                    type="number"
                    inputMode="decimal"
                    step="0.5"
                    min="5"
                    max="60"
                    value={targetPct}
                    onChange={(e) => setTargetPct(e.target.value)}
                    className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium tabular-nums outline-none focus:border-blue-600"
                  />
                  <p className="mt-1 text-xs text-black/55">
                    Industry healthy: 28–32%. Higher = lower margin, lower price.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-black/10 bg-black/[0.02] p-3 text-xs">
                <div className="mb-1 font-medium text-black/60">
                  AI will use
                </div>
                <div className="text-black/70">
                  <span className="font-medium tabular-nums">{(items || []).length}</span> items in your catalog as ingredient candidates
                  {(items || []).length === 0 && (
                    <span className="ml-2 text-red-600">— add items in Settings first</span>
                  )}
                </div>
              </div>

            </>
          )}

          {/* GENERATING */}
          {phase === 'generating' && (
            <KitchenLoader
              messages={[
                'Pulling the ingredients\u2026',
                'Costing every component\u2026',
                'Tasting for menu price\u2026',
                'Plating the recipe\u2026',
              ]}
            />
          )}

          {/* REVIEW */}
          {phase === 'review' && proposal && (
            <>
              {/* Recipe name (editable) */}
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="r-name">
                  Recipe name
                </label>
                <input
                  id="r-name"
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="w-full rounded-lg border border-black/15 px-4 py-3 text-base font-medium outline-none focus:border-blue-600"
                />
              </div>

              {/* Approach + warnings */}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-sm leading-relaxed text-emerald-900">
                <div className="text-[11px] font-medium text-emerald-900/70">
                  AI's approach
                </div>
                <div className="mt-1">{proposal.notes}</div>
                {proposal.warnings && proposal.warnings.length > 0 && (
                  <div className="mt-2 border-t border-emerald-200/60 pt-2">
                    <div className="text-[11px] font-medium text-amber-900/70">
                      Heads up
                    </div>
                    <ul className="mt-1 list-disc space-y-0.5 pl-5 text-amber-900">
                      {proposal.warnings.map((w, i) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Live costing block */}
              <div className="rounded-xl border border-black/10 bg-white p-4">
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div className="rounded-lg bg-black/[0.03] px-2 py-2">
                    <div className="text-[10px] font-medium text-black/55">Total recipe cost</div>
                    <div className="mt-0.5 text-base font-medium tabular-nums">
                      {formatMoney(liveCost.total)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-black/[0.03] px-2 py-2">
                    <div className="text-[10px] font-medium text-black/55">Per serving</div>
                    <div className="mt-0.5 text-base font-medium tabular-nums">
                      {formatMoney(liveCost.perServing)}
                    </div>
                  </div>
                  <div className="rounded-lg bg-emerald-50 px-2 py-2">
                    <div className="text-[10px] font-medium text-emerald-900/60">Suggested menu price</div>
                    <div className="mt-0.5 text-base font-medium tabular-nums text-emerald-800">
                      {formatMoney(liveCost.price)}
                    </div>
                  </div>
                </div>
                {!liveCost.known && (
                  <p className="mt-2 text-[11px] text-amber-700">
                    One or more components have $0 cost in your catalog —
                    set the cost in Settings → Items to get accurate COGS.
                  </p>
                )}
              </div>

              {/* Components list — editable */}
              <div className="space-y-2">
                <div className="text-[11px] font-medium text-black/60">
                  Components ({visibleComponents.length})
                </div>
                {visibleComponents.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/60">
                    No components — try regenerating with a more specific dish description.
                  </div>
                ) : (
                  visibleComponents.map((c) => {
                    const matched = !!c.item_id && itemsById.has(c.item_id)
                    const item = itemsById.get(c.item_id)
                    const lineCost = effectiveUnitCost(item, c.unit) * Number(c.quantity || 0)
                    return (
                      <div
                        key={c._id}
                        className={`rounded-xl border p-3 ${
                          matched ? 'border-black/15 bg-white' : 'border-amber-300 bg-amber-50/40'
                        }`}
                      >
                        <div className="mb-1 flex items-center justify-between gap-2">
                          {matched ? (
                            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                              Matched
                            </span>
                          ) : (
                            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                              Not in catalog
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => removeComponent(c._id)}
                            aria-label="Remove component"
                            className="min-h-[36px] min-w-[36px] rounded-lg px-2 text-black/40 hover:bg-red-50 hover:text-red-600"
                          >
                            ✕
                          </button>
                        </div>
                        <div className="text-sm font-medium">
                          {item?.name ?? c.item_name}
                        </div>
                        {c.rationale && (
                          <div className="mt-0.5 text-xs italic text-black/55">
                            {c.rationale}
                          </div>
                        )}
                        <div className="mt-2 grid grid-cols-3 gap-2">
                          <div>
                            <label className="text-[10px] font-medium text-black/55">Qty</label>
                            <input
                              type="number"
                              inputMode="decimal"
                              step="0.01"
                              min="0"
                              value={c.quantity}
                              onChange={(e) => updateComponent(c._id, { quantity: e.target.value })}
                              className="w-full rounded-lg border border-black/15 px-2 py-2 text-base font-medium tabular-nums outline-none focus:border-blue-600"
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-black/55">Unit</label>
                            <div className="rounded-lg border border-black/10 bg-black/[0.02] px-2 py-2 text-base text-black/70">
                              {c.unit || item?.unit || '—'}
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-black/55">Line cost</label>
                            <div className="rounded-lg border border-black/10 bg-black/[0.02] px-2 py-2 text-base font-medium tabular-nums text-black/85">
                              {formatMoney(lineCost)}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })
                )}
              </div>

              <button
                type="button"
                onClick={() => setPhase('inputs')}
                className="w-full rounded-lg border border-blue-200 bg-blue-50/40 px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-50"
              >
                Don't like it? Regenerate with different description
              </button>
            </>
          )}

          {/* SUBMITTING */}
          {phase === 'submitting' && (
            <div className="flex flex-col items-center gap-2 py-8 text-sm font-medium text-black/75">
              <svg className="h-5 w-5 animate-spin text-blue-600" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
              </svg>
              Saving recipe{'\u2026'}
            </div>
          )}

          {/* ERROR */}
          {phase === 'error' && (
            <div className="space-y-3">
              <p className="text-sm text-black/70">Something went wrong. Try again?</p>
              <button
                type="button"
                onClick={() => setPhase('inputs')}
                className="min-h-btn w-full rounded-lg border border-blue-600 bg-white px-4 py-3 text-base font-medium text-blue-700 hover:bg-blue-50"
              >
                Try again
              </button>
            </div>
          )}
        </div>

        {/* Sticky CTA */}
        {phase === 'inputs' && (
          <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!description.trim() || (items || []).length === 0}
              className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Build recipe with AI
            </button>
          </div>
        )}
        {phase === 'review' && (
          <RecipeAcceptCTA
            count={visibleComponents.filter((c) => c.item_id).length}
            cost={liveCost.total}
            onAccept={handleAccept}
          />
        )}
      </div>
    </div>
  )
}

// Sticky CTA wrapper — needs access to createRecipe, which we get
// from the parent component via prop drilling through onAccept.
// Refactored as a small component just so the sticky styling stays
// out of the main render tree.
function RecipeAcceptCTA({ count, cost, onAccept }) {
  return (
    <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
      <button
        type="button"
        onClick={onAccept}
        disabled={count === 0}
        className="min-h-btn w-full rounded-lg bg-blue-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {count === 0
          ? 'Need at least one matched component'
          : `Save recipe \u00b7 ${count} ${count === 1 ? 'component' : 'components'} \u00b7 ${formatMoney(cost)}`}
      </button>
    </div>
  )
}
